/* SAVE THAT THOUGHT — beside Send in the Ask Brill box (her ask, 2026-10-21,
 * from the C1 mockup's "Save for later"). Return still sends to Brill; this
 * files what she typed into her Library instead, exactly as she typed it.
 *
 * No Brill call: it is one write to `library_items` through the same shared
 * store every Library screen reads (utils/library.js). A save OPENS THE
 * LIBRARY over the chat on that kind, the item lit (her ask: see it land,
 * then ✕ out of it) — `onSaved(item)` hands the caller what was saved. The menu opens UPWARD (the box sits at the
 * bottom of the screen) with the Library's own four kinds, in its order.
 *
 * Never a dead button: with nothing typed it says so instead of saving. A
 * save that fails keeps her words in the box and says so in amber — "Saved"
 * and silence must never look the same. A testimonial lands without
 * permission ticked; the Library already flags that before it is used.
 */
import { useEffect, useRef, useState } from "react";
import { Bookmark } from "lucide-react";
import { useLibrary, LIBRARY_KINDS } from "../utils/library";

/** The Library record a thought becomes: her words as typed, nothing added. */
export function thoughtItem(kind, text) {
  return { kind, text: String(text || "").trim() };
}

export function SaveThought({ text, onSaved, isMobile }) {
  const { save } = useLibrary();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(null); // a warning only: what was saved shows in the Library
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);
  const empty = !String(text || "").trim();

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  const press = () => {
    setNote(null);
    if (empty) { setNote("Type your thought first, then save it."); return; }
    setOpen((v) => !v);
  };

  const pick = async (k) => {
    setOpen(false);
    setSaving(true);
    const saved = await save(thoughtItem(k.id, text));
    setSaving(false);
    if (!saved) { setNote("Couldn't save that. Your words are still in the box — try again."); return; }
    onSaved(saved);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0 }}>
      {open && (
        <div role="menu" aria-label="Save to your Library as"
          style={{
            position: "absolute", right: 0, bottom: "calc(100% + 6px)", zIndex: 20, minWidth: 180,
            background: "var(--bg-surface)", border: "1px solid var(--border-hairline)", borderRadius: 12,
            boxShadow: "var(--shadow-menu)", padding: 6, display: "flex", flexDirection: "column", gap: 2,
          }}>
          <div style={{ padding: "4px 10px 6px", fontSize: 11, color: "var(--text-secondary)" }}>Save to your Library as</div>
          {LIBRARY_KINDS.map((k) => (
            <button key={k.id} role="menuitem" onClick={() => pick(k)}
              style={{
                textAlign: "left", border: "none", background: "transparent", borderRadius: 8,
                padding: "9px 10px", fontSize: 13.5, color: "var(--text-primary)", cursor: "pointer", fontFamily: "inherit",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--accent-quiet)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              {k.label}
            </button>
          ))}
        </div>
      )}
      {note && !open && (
        <div role="status"
          style={{
            position: "absolute", right: 0, bottom: "calc(100% + 6px)", zIndex: 20, whiteSpace: "nowrap",
            padding: "7px 11px", borderRadius: 10, fontSize: 12.5,
            background: "var(--bg-surface)", boxShadow: "var(--shadow-menu)",
            border: "1px solid var(--warn-line)", color: "var(--warn-text)",
          }}>
          {note}
        </div>
      )}
      <button onClick={press} disabled={saving} aria-haspopup="menu" aria-expanded={open}
        title="Save that thought to your Library"
        style={{
          display: "flex", alignItems: "center", gap: 6, borderRadius: 10, cursor: saving ? "wait" : "pointer",
          padding: isMobile ? "12px 13px" : "11px 14px", fontSize: 13, fontWeight: 500, fontFamily: "inherit",
          background: open ? "var(--accent-quiet)" : "transparent",
          border: `1.5px solid ${open ? "var(--accent)" : "var(--border-strong)"}`,
          color: empty ? "var(--text-secondary)" : "var(--accent)",
        }}>
        <Bookmark size={15} />
        {!isMobile && <span>{saving ? "Saving…" : "Save that thought"}</span>}
      </button>
    </div>
  );
}
